import { use, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AccountUsage as UsageView,
  type AccountReadState,
  type AccountUsageData,
  type AccountUsagePeriod,
} from "@softmax/product-ux/account";
import {
  QueryViewerContext,
  useDiscardUnavailableData,
} from "@softmax/web-features/data";
import { accountUsageQueryOptions } from "./usage.queries";
import { buildUsageView } from "./usage-format";

const dollars = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

export function PersonalUsage() {
  const viewerId = use(QueryViewerContext);
  const [period, setPeriod] = useState<AccountUsagePeriod>("week");
  const options = accountUsageQueryOptions({ viewerId, period });
  const query = useQuery(options);
  const unavailable = useDiscardUnavailableData({
    queryKey: options.queryKey,
    error: query.error,
  });
  let state: AccountReadState<AccountUsageData> = { status: "loading" };

  if (query.data && !unavailable) {
    const usage = buildUsageView(query.data);
    state = {
      status: "ready",
      data: {
        total: dollars.format(usage.totalUsd),
        kinds: usage.isEmpty
          ? []
          : usage.kinds.map((kind) => ({
              id: kind.kind,
              label: kind.label,
              amount:
                kind.amountUsd === null ? "—" : dollars.format(kind.amountUsd),
              detail: kind.detail,
            })),
        coworlds: usage.coworlds.map((row) => ({
          id: row.key,
          name: row.coworld,
          llm: dollars.format(row.llmEstimatedCostUsd),
          compute: dollars.format(row.computeCostUsd),
          total: dollars.format(row.estimatedTotalCostUsd),
        })),
      },
    };

    if (query.error)
      state = {
        ...state,
        refresh: { status: "error", message: query.error.message },
      };
    else if (query.isFetching)
      state = { ...state, refresh: { status: "refreshing" } };
  } else if (query.error)
    state = { status: "error", message: query.error.message };

  return (
    <UsageView
      state={state}
      period={period}
      onPeriodChange={setPeriod}
      onRetry={() => {
        if (!query.isFetching) void query.refetch();
      }}
    />
  );
}
